// Explicit React import: consumers transform this file with their OWN jest/
// vite configs, and apps/app's jest uses the classic JSX runtime (React must
// be in scope). Harmless under the automatic runtime.
import * as React from 'react';
import type { Block, ChildBlock, ColumnsBlock, EmbedBlock } from '../schemas/block';
import { sanitizeAuthoredHtml } from '../utils/sanitizeHtml';

/**
 * The single block renderer, used by BOTH the public site (apps/app) and the
 * admin canvas editor (apps/admin). One implementation is what makes
 * editor/site parity structural rather than aspirational — the drift bugs
 * this replaces (width, typography) all came from two renderers disagreeing.
 *
 * Rules of this file (same discipline as styles/quillContent.css):
 * - Plain JSX + plain CSS classes only. No MUI, no Tailwind — the two apps
 *   ship different versions of both. All visuals live in
 *   packages/shared/styles/blockContent.css.
 * - richtext renders inside `.ql-editor` so quillContent.css keeps governing
 *   authored typography everywhere.
 * - Never import app-specific code from here.
 */

// YouTube (watch/short/embed/youtu.be) and Vimeo URLs → embeddable src.
// Anything unrecognized is iframed as-is (Maps embed URLs are already
// iframe-ready). Exported for tests.
export function toEmbedSrc(url: string): string {
  const yt = url.match(
    /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/i
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

function RichtextView({ html }: { html: string }) {
  return (
    <div
      className="ql-editor staty-block staty-block--richtext"
      // Org-authored HTML: sanitized at render (tranche-2 gap 7) — scripts/
      // handlers stripped, provider iframes (YouTube/Vimeo/Maps) kept.
      dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(html) }}
    />
  );
}

function EmbedView({ block }: { block: EmbedBlock }) {
  return (
    <div className="staty-block staty-block--embed">
      <iframe
        src={toEmbedSrc(block.url)}
        title="Embedded content"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
}

// Column widths as a grid-template value ("2fr 1fr"). Exposed for the canvas
// editor, which renders its own columns grid but must compose identically.
export function columnsTemplate(block: ColumnsBlock): string {
  return block.columns.map((c) => `${c.span ?? 1}fr`).join(' ');
}

function ColumnsView({ block }: { block: ColumnsBlock }) {
  return (
    <div
      className="staty-block staty-block--columns"
      data-cols={block.columns.length}
      // Custom property, NOT grid-template-columns directly: blockContent.css
      // consumes it only inside the ≥640px media query, so mobile stacking
      // (one column) survives — an inline grid-template would override it.
      style={{ ['--staty-cols' as string]: columnsTemplate(block) }}
    >
      {block.columns.map((col) => (
        <div key={col.id} className="staty-block-column">
          {col.blocks.map((child) => (
            <BlockView key={child.id} block={child} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function BlockView({ block }: { block: Block | ChildBlock }) {
  switch (block.type) {
    case 'richtext':
      return <RichtextView html={block.html} />;
    case 'image': {
      // widthPct (free resize) overrides the preset; the preset CSS keys off
      // data-width, which we drop when a pct is set so the rules can't fight.
      const img = (
        <img
          src={block.src}
          alt={block.alt || ''}
          loading="lazy"
          style={block.widthPct ? { width: `${block.widthPct}%` } : undefined}
        />
      );
      return (
        <figure
          className="staty-block staty-block--image"
          data-width={block.widthPct ? undefined : block.width || 'full'}
          data-align={block.alignment || 'center'}
        >
          {block.link ? <a href={block.link}>{img}</a> : img}
        </figure>
      );
    }
    case 'embed':
      return <EmbedView block={block} />;
    case 'divider':
      return <hr className="staty-block staty-block--divider" />;
    case 'button': {
      // A button pointing at a PDF (the editor's "Document" entry) opens in
      // a new tab — same-tab navigation to the file would leave the site.
      const isPdf = /\.pdf($|[?#])/i.test(block.url);
      return (
        <div
          className="staty-block staty-block--button"
          data-align={block.alignment || 'left'}
        >
          <a
            className={`staty-btn staty-btn--${block.style || 'primary'}`}
            href={block.url}
            target={isPdf ? '_blank' : undefined}
            rel={isPdf ? 'noopener noreferrer' : undefined}
          >
            {block.label}
          </a>
        </div>
      );
    }
    case 'columns':
      return <ColumnsView block={block} />;
    default:
      // Unknown type (forward-compat: an old bundle meeting a newer block).
      // Render nothing rather than crash the page.
      return null;
  }
}

export function BlockRenderer({ blocks }: { blocks: Block[] | undefined | null }) {
  if (!Array.isArray(blocks) || blocks.length === 0) return null;
  return (
    <>
      {blocks.map((block) => (
        <BlockView key={block.id} block={block} />
      ))}
    </>
  );
}

export default BlockRenderer;
